import _ from 'lodash';
import { getLogger } from '../commons/logger';
import { getPassedTests } from './reporterUtils';
import type { IReporter } from './IReporter';
import type { RunnerOptions } from '../runOptions';
import type { ExecutionList } from '../runners/TestPlanRunner';
import type { TestRunStatusTestResult } from '../testRunStatus';

const logger = getLogger('debug-reporter');

const optionsKeysToMask = [
    'allGrids',
    'authData',
    'gridData',
    'perfecto',
    'projectData',
    'runParams',
    'testobjectSauce',
    'token',
    'userData',
    'userParamsData',

    'awsAccessKeyId',
    'awsSecretAccessKey',
] satisfies Array<(keyof RunnerOptions) | (string & {})>;

export class DebugReporter implements IReporter {
    constructor(private readonly options: RunnerOptions) {}

    public onTestStarted(test: TestRunStatusTestResult, workerId: number) {
        logger.info('Test Started', { testId: test.testId, testName: test.name, resultId: test.resultId, workerId });
    }

    public onTestFinished(test: TestRunStatusTestResult, workerId: number) {
        const gridData = this.options.gridData || {};
        const provider = gridData.provider;
        const host = gridData.host;
        const gridId = gridData.gridId;
        const gridType = gridData.type;
        const failedGetBrowserAttempts = gridData.failedGetBrowserAttempts;
        logger.info('Test Finished', {
            testId: test.testId,
            testName: test.name,
            resultId: test.resultId,
            success: test.success,
            duration: test.duration,
            browser: this.options.browser,
            companyId: this.options.company!.companyId,
            grid: { provider, host, failedGetBrowserAttempts, id: gridId, type: gridType },
            workerId,
        });
    }

    private get sanitizedOptionsForLogging() {
        return {
            ..._.omit(this.options, optionsKeysToMask),
            company: _.pick(this.options.company, ['companyId', 'name', 'planType']),
            activePlan: this.options.company!.activePlan,
        };
    }

    public onTestPlanStarted(beforeTests: ExecutionList, tests: ExecutionList, afterTests: ExecutionList, testPlanName: string, executionId: string, isAnonymous: boolean, configName: string | null) {
        const args = stripTokenFromConsoleArguments(process.argv);

        logger.info('Test Plan Started', {
            executionId,
            testPlanName,
            isAnonymous,
            configName,
            options: this.sanitizedOptionsForLogging,
            args,
        });
    }

    public onTestPlanFinished(testResults: { [resultId: string]: TestRunStatusTestResult }, testPlanName: string, duration: number, executionId: string, isAnonymous: boolean) {
        const passed = getPassedTests(testResults).length;
        const failed = Object.keys(testResults).length - passed;

        logger.info('Test Plan Finished', {
            executionId,
            testPlanName,
            isAnonymous,
            passed,
            failed,
            options: this.sanitizedOptionsForLogging,
            duration,
        });
    }
}

function stripTokenFromConsoleArguments(args: string[]) {
    const indexOfTokenFlag = args.indexOf('--token');

    if (indexOfTokenFlag !== -1) {
        try {
            const newArgs = args.slice();
            newArgs.splice(indexOfTokenFlag, 2);

            return newArgs;
        } catch (e) {
            /* empty */
        }
    }

    return args;
}
